import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ReviewWidget from "@/components/ReviewWidget";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { ChevronRight, CheckCircle2, Apple, HeartPulse, Scale, AlertCircle, Clock, Target, ShieldCheck, Activity } from "lucide-react";
import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";

export default function ChiroThin() {
  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">
      <SEOHead 
        title="ChiroThin Weight Loss | Woods Spine & Wellness | Longview TX"
        description="Experience the clinically-proven ChiroThin weight loss protocol at Woods Spine & Wellness in Longview, TX. Supervised by chiropractors for dramatic, sustainable results."
        ogImage="https://vibe.filesafe.space/1774569058151009111/assets/095d771b-c840-44cf-856f-35be93ca1506.png"
        canonical="https://woodsspine.com/longview-chirothin-weight-loss"
      />
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative pt-20 pb-32 lg:pt-32 lg:pb-40 overflow-hidden bg-primary">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://wsrv.nl/?url=https://vibe.filesafe.space/1774569058151009111/assets/095d771b-c840-44cf-856f-35be93ca1506.png&w=1920&output=webp&q=80" 
              alt="Healthy person smiling outdoors representing weight loss success" 
              className="w-full h-full object-cover object-center opacity-40"
              fetchPriority="high"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent"></div>
          </div>
          
          <div className="container relative z-10 mx-auto px-4">
            <div className="max-w-2xl text-white">
              <div className="inline-flex items-center rounded-full border border-white/30 bg-white/10 px-3 py-1 text-sm font-medium backdrop-blur-sm mb-6">
                <span className="flex h-2 w-2 rounded-full bg-accent mr-2"></span>
                Doctor-Supervised Weight Loss
              </div>
              <img src="https://wsrv.nl/?url=https://chirothin.com/logo.png&w=400&output=webp&q=80" alt="ChiroNutraceutical Logo" className="h-16 mb-6 bg-white/90 p-2 rounded-lg" fetchPriority="high" decoding="async" />
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 [text-shadow:_0_1px_3px_rgba(0,0,0,0.5)]">
                Transform Your Body with <span className="text-accent">ChiroThin™</span>
              </h1>
              <p className="text-lg md:text-xl mb-8 text-white/90 max-w-xl [text-shadow:_0_1px_2px_rgba(0,0,0,0.5)]">
                Experience the clinically-proven weight loss protocol, supervised by our expert chiropractors and designed to help you achieve dramatic, sustainable results.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-accent text-white hover:bg-accent/90 border-0" asChild>
                  <Link to="/specials">
                    Claim 9-Day Trial for $149 <ChevronRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* WHAT IT IS */}
        <section className="py-16 bg-secondary/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">What is the ChiroThin™ Program?</h2>
            <div className="prose prose-lg text-muted-foreground">
              <p>
                The ChiroThin Weight Loss Program is a revolutionary, doctor-supervised approach to achieving significant and sustainable weight loss. Unlike fad diets that rely on starving your body, artificial shakes, or expensive pre-packaged meals, ChiroThin is a comprehensive program that combines a proprietary dietary supplement with a healthy, specific diet of real food purchased from your local grocery store.
              </p>
              <p>
                At the core of the program is the ChiroThin nutritional support formula. This specialized blend contains amino acids, vitamins, and cell salts that are specifically formulated to support your body's natural processes. It aids in fatty acid transportation, stabilizes blood sugar, and boosts metabolism. Most importantly, it helps your body burn its stored fat for energy while protecting your lean muscle mass.
              </p>
              <p>
                Because this program requires a doctor's supervision, you are never left to figure it out on your own. You will have regular check-ins with our clinic to monitor your progress, ensure you are losing weight safely, and learn the behavioral habits necessary to maintain your new, healthy weight long after the initial program is completed.
              </p>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">How ChiroThin Works</h2>
            <p className="text-lg text-muted-foreground mb-8">
              The ChiroThin program is a structured, multi-phase protocol designed to reset your metabolism and retrain your eating habits. Here is how the process works:
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4">
                    <span className="font-bold text-xl">1</span>
                  </div>
                  <h3 className="text-xl font-bold text-primary mb-2">Comprehensive Evaluation</h3>
                  <p className="text-muted-foreground">Your journey begins with a thorough consultation and body composition analysis. Our doctors will review your health history to ensure you are a safe candidate for the program and establish your baseline metrics.</p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4">
                    <span className="font-bold text-xl">2</span>
                  </div>
                  <h3 className="text-xl font-bold text-primary mb-2">The Dietary Protocol</h3>
                  <p className="text-muted-foreground">You will follow a specific, low-glycemic, anti-inflammatory diet consisting of lean proteins, vegetables, and fruit. You will eat real food that you prepare yourself, avoiding sugars, starches, and processed foods.</p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4">
                    <span className="font-bold text-xl">3</span>
                  </div>
                  <h3 className="text-xl font-bold text-primary mb-2">The Supplement Drops</h3>
                  <p className="text-muted-foreground">Alongside the diet, you will take the ChiroThin sublingual drops daily. This proprietary formula suppresses your appetite, helps your body convert stored fat into energy, and prevents the loss of lean muscle mass during the weight loss phase.</p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4">
                    <span className="font-bold text-xl">4</span>
                  </div>
                  <h3 className="text-xl font-bold text-primary mb-2">Maintenance & Lifestyle</h3>
                  <p className="text-muted-foreground">After the initial weight loss phase, you enter the maintenance phase. We will slowly reintroduce a wider variety of foods while teaching you how to maintain your new weight through sustainable lifestyle choices, avoiding the "yo-yo" dieting effect.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* WHO IT'S FOR */}
        <section className="py-16 bg-secondary/30">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-10 text-center">Who is ChiroThin For?</h2>
            
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h3 className="text-2xl font-bold text-primary mb-6 flex items-center">
                  <CheckCircle2 className="h-6 w-6 text-accent mr-2" /> Ideal Candidates
                </h3>
                <p className="text-muted-foreground mb-6">
                  ChiroThin is designed for individuals who are serious about making a change and are ready to commit to a structured program. It is highly effective for:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-accent mr-2 shrink-0 mt-0.5" /> Individuals needing to lose 15 to 35+ pounds safely</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-accent mr-2 shrink-0 mt-0.5" /> Those who have failed with traditional diets or calorie counting</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-accent mr-2 shrink-0 mt-0.5" /> Patients experiencing joint pain exacerbated by excess weight</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-accent mr-2 shrink-0 mt-0.5" /> People looking for a program that uses real, grocery-store food</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-accent mr-2 shrink-0 mt-0.5" /> Individuals who need accountability and doctor supervision to stay on track</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-primary mb-6 flex items-center">
                  <AlertCircle className="h-6 w-6 text-destructive mr-2" /> Contraindications
                </h3>
                <p className="text-muted-foreground mb-6">
                  Because this is a specific, low-calorie dietary protocol, it is not safe for everyone. You are NOT a candidate for the ChiroThin program if you:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-destructive mr-2 shrink-0 mt-0.5" /> Are currently pregnant or nursing</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-destructive mr-2 shrink-0 mt-0.5" /> Have active, untreated cancer</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-destructive mr-2 shrink-0 mt-0.5" /> Suffer from an active eating disorder (like anorexia or bulimia)</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-destructive mr-2 shrink-0 mt-0.5" /> Have severe, uncontrolled metabolic or endocrine disorders</li>
                </ul>
                <p className="text-sm text-muted-foreground mt-4 italic">
                  *Our doctors will review your complete medical history and current medications during your initial consultation to ensure this program is safe for you.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* WHAT TO EXPECT */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">What to Expect During the Program</h2>
            <div className="prose prose-lg text-muted-foreground mb-8">
              <p>
                Committing to ChiroThin means committing to a lifestyle change, but you will have our team guiding you every step of the way. Here is what you can expect:
              </p>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-8">
              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <Clock className="h-8 w-8 text-accent mb-4" />
                  <h3 className="text-xl font-bold text-primary mb-2">Duration</h3>
                  <p className="text-muted-foreground">The active weight loss phase of the ChiroThin program typically lasts for 6 weeks (42 days). This is followed by a structured maintenance phase to help your body lock in its new weight.</p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <Activity className="h-8 w-8 text-accent mb-4" />
                  <h3 className="text-xl font-bold text-primary mb-2">What You'll Feel</h3>
                  <p className="text-muted-foreground">During the first few days, you may experience mild hunger or detox symptoms as your body adjusts. However, the ChiroThin drops quickly help suppress appetite. Most patients report feeling highly energetic and clear-headed throughout the program.</p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <Target className="h-8 w-8 text-accent mb-4" />
                  <h3 className="text-xl font-bold text-primary mb-2">Clinic Visits</h3>
                  <p className="text-muted-foreground">You will visit our clinic weekly for a brief, 10-15 minute check-in. During these visits, we will track your weight, take body measurements, review your food journal, and answer any questions you have to ensure you stay on track.</p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <ShieldCheck className="h-8 w-8 text-accent mb-4" />
                  <h3 className="text-xl font-bold text-primary mb-2">Exercise Guidelines</h3>
                  <p className="text-muted-foreground">Unlike many programs that require grueling workouts, heavy exercise is actually discouraged during the 6-week weight loss phase, as it can interfere with the protocol. Light walking or yoga is acceptable.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* LOCAL RELEVANCE */}
        <section className="py-12 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Your Partner in Health in Longview, TX</h2>
            <p className="text-lg text-primary-foreground/90 leading-relaxed">
              At Woods Spine & Wellness, we know that excess weight is a major contributing factor to back pain, joint degeneration, and overall poor health. We brought the ChiroThin program to our Longview, TX clinic because we saw a need in the East Texas community for a safe, doctor-supervised weight loss solution that actually works. We are not just here to treat your pain; we are here to help you achieve total body wellness. Let us help you take control of your health right here in your local community.
            </p>
          </div>
        </section>

        {/* FAQ SECTION */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-8">
              <div className="border-b border-border pb-6">
                <h3 className="text-xl font-bold text-primary mb-3">Is ChiroThin safe?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  Yes, ChiroThin is extremely safe when supervised by a qualified physician. The proprietary nutritional drops are manufactured in the United States in FDA-inspected facilities. Because you are eating real food and being monitored weekly by our doctors, the risks associated with starvation diets or unverified supplements are eliminated.
                </p>
              </div>
              <div className="border-b border-border pb-6">
                <h3 className="text-xl font-bold text-primary mb-3">How much weight can I expect to lose?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  While individual results vary based on starting weight, adherence to the protocol, and metabolic factors, many patients lose between 15 and 35 pounds during the 6-week program. Our doctors will discuss realistic expectations during your consultation.
                </p>
              </div>
              <div className="border-b border-border pb-6">
                <h3 className="text-xl font-bold text-primary mb-3">Is the ChiroThin program covered by insurance?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  Weight loss programs, including ChiroThin, are generally considered elective wellness treatments and are not covered by major medical insurance or Medicare. However, you can often use HSA (Health Savings Account) or FSA (Flexible Spending Account) funds to pay for the program. We also offer affordable financing options.
                </p>
              </div>
              <div className="border-b border-border pb-6">
                <h3 className="text-xl font-bold text-primary mb-3">Will I be starving on this diet?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  No. While the diet is calorie-restricted, the ChiroThin supplement drops are specifically formulated to help suppress appetite and prevent cravings. Furthermore, because the formula helps your body utilize its stored fat for energy, your body is actually receiving plenty of "calories" from its own fat reserves, keeping your energy levels high.
                </p>
              </div>
              <div className="pb-2">
                <h3 className="text-xl font-bold text-primary mb-3">Do I have to buy expensive pre-packaged meals?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  No! This is one of the biggest benefits of ChiroThin. You will eat real, whole foods—like chicken, fish, beef, vegetables, and fruit—that you purchase from your local grocery store. This makes the program much more affordable and teaches you how to shop and cook healthily for long-term success.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-secondary/30 text-center border-t border-border">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">Ready to Start Your Journey?</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Take the first step toward a healthier, lighter you. Schedule your consultation today to find out if the ChiroThin program is right for you.
            </p>
            <Button size="lg" className="bg-accent text-white hover:bg-accent/90 border-0" asChild>
              <Link to="/appointment-scheduling">
                Book Your Consultation Now <ChevronRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </section>

        <ReviewWidget />
      </main>
      <Footer />
    </div>
  );
}
